import React, {useEffect, useState} from 'react';
import { useTranslation } from 'react-i18next';
import styles from './companies.module.scss';
import { useAppDispatch } from "../../../hooks/useAppDispatch";
import {
    fetchCompaniesData,
    selectFilteredTenantData,
    setSearchQuery,
    setSelectedCities
} from "../../../services/reducers/companies";
import { useAppSelector } from "../../../hooks/useAppSelector";
import SearchBar from "../../ui-kit/search-bar/search-bar";
import Dropdown from "../../ui-kit/dropdown/dropdown";
import CompanyCard from "./companies-card/companies-card";
import CustomScrollbar from "../../ui-kit/scrollbar/scrollbar";
import { ExportToExcel } from '../../../utils/export-to-excel';
import { removeIndustrialCity } from '../../../utils/helpers-for-cities';
import {useNavigate, useSearchParams} from 'react-router-dom';
import usePopStateScroll from "../../../hooks/usePopStateScroll";
import useDebounce from "../../../hooks/useDebounce";

const Companies: React.FC = () => {
    const dispatch = useAppDispatch();
    const { searchQuery, cities, selectedCities } = useAppSelector(state => state.companies);
    const filteredTenantData = useAppSelector(selectFilteredTenantData);
    const layers = useAppSelector(state => state.map.layers)
    const [searchParams, setSearchParams] = useSearchParams()
    const [localSearch, setLocalSearch] = useState(searchQuery)
    const debouncedSearch = useDebounce(localSearch, 1000);
    const { t } = useTranslation();
    const navigate = useNavigate();
    useEffect(() => {
        if (layers.length > 0) {
            dispatch(fetchCompaniesData());
        }
    }, [dispatch, layers]);

    useEffect(() => {
        dispatch(setSearchQuery(debouncedSearch));
        const params = new URLSearchParams(searchParams);
        const currentQuery = new URLSearchParams(window.location.search).get("q") || "";

        if (debouncedSearch.trim() !== currentQuery.trim()) {
            params.set("q", debouncedSearch);
            navigate(`?${params.toString()}`);
        }
    }, [debouncedSearch]);

    useEffect(() => {
        if (searchParams.has("cities") && searchParams.get("cities")) {
            const citiesFromUrl = searchParams.get("cities")!.split(",").map((item) => +item);
            dispatch(setSelectedCities(citiesFromUrl));
        } else {
            dispatch(setSelectedCities([]));
        }
    }, [searchParams, dispatch]);


    useEffect(() => {
        if (searchParams.has("q")) {
            const newQuery = searchParams.get("q")!;
            setLocalSearch(newQuery);
            dispatch(setSearchQuery(newQuery));
        } else {
            setLocalSearch('');
            dispatch(setSearchQuery(''));
        }
    }, [searchParams, dispatch]);


    const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setLocalSearch(e.target.value);
    };

    const handleClearSearch = () => {
        setLocalSearch('');
        dispatch(setSearchQuery(''));
        const newParams = new URLSearchParams(searchParams.toString());
        newParams.delete("q");
        setSearchParams(newParams);
    };

    const handleCityChange = (cities: number | number[] | null) => {
        if (typeof cities === "number" || !cities) return;
        dispatch(setSelectedCities(cities));
        setSearchParams((prevParams) => {
            const newParams = new URLSearchParams(prevParams);
            if (cities && cities.length) {
                newParams.set("cities", cities.join(","));
            } else {
                newParams.delete("cities");
            }
            return newParams;
        });
    };

    const isSearchActive = searchQuery.trim().length > 0 || (selectedCities && selectedCities.length);
    const exportToExcel = () => {
        const customSheet = filteredTenantData.map((d, idx)=>
            ({
                "No.": idx + 1,
                "Estate Name": d.estateEn,
                "Tenant Name": { text: `${d.name_en}\n${d.name_ar}`, hyperlink: `https://ems.peie.om/newDir/TenantDetails.aspx?lang=&tid=${d.tenant_id}`},
                "Established date": d.date_of_establish,
                "Product/Service": d.prodtype.join('\n'),
                "Email": d.email,
                "Mobile phone": d.mobile,
                "Other phones": [d.phone, d.other_phones].filter(p => p).join(', '),
                "Website ": d.website
            })
        );
        const columns = {
            "No.": 8,
            "Estate Name": 22,
            "Tenant Name": 30,
            "Established date": 17,
            "Product/Service": 35,
            "Email": 35,
            "Mobile phone": 17,
            "Other phones": 17,
            "Website ": 32
        }
        ExportToExcel(columns, customSheet, 'Companies', 'CompaniesExport.xlsx', ["Tenant Name"])
    };

    usePopStateScroll({ param: 'tid', prefix: 'company-closed-card-' });

    return (
        <div className={styles.companies}>
            <div className={styles.staticContent}>
                <div className={styles.header}>
                    <h1>{t('companies.header')}</h1>
                    <span className={`${isSearchActive ? styles.activeSearch : ''}`}>
                    {t('companies.companiesAvailable', {count: filteredTenantData.length})}
                </span>
                </div>
                <div>
                    <SearchBar
                        placeholder={t('companies.searchPlaceholder')}
                        searchValue={localSearch}
                        onSearchChange={handleSearchChange}
                        onClearSearch={handleClearSearch}
                    />
                    <Dropdown
                        className={styles.citiesDropdown}
                        value={selectedCities}
                        onChange={(city) => handleCityChange(city)}
                        onClear={() => handleCityChange([])}
                        placeholder={t('companies.cityPlaceholder')}
                        listType="checkbox"
                        options={cities}
                        customDisplay={(value)=>removeIndustrialCity(value).cleanedName} />
                </div>
            <div className={styles.exportCompanies}>
                <a onClick={()=>exportToExcel()}>{t('companies.exportLink')}</a>
            </div>
            </div>
            <CustomScrollbar marginRight='13px'>
                <div className={styles.listCards}>
                    {filteredTenantData.map(tenant => {
                        return (
                            <CompanyCard
                                key={tenant.tenant_id}
                                tenant={tenant}
                            />
                        );
                    })}
                </div>
            </CustomScrollbar>
        </div>
    );
};

export default Companies;
